// Removed drag and drop imports since we're using selection mode only
import {
	Close as CloseIcon,
	Delete as DeleteIcon,
	Merge as MergeIcon,
	Pause as PauseIcon,
	PlayArrow as PlayIcon,
	Refresh as RefreshIcon,
	Assessment as ResultsIcon,
	Share as ShareIcon,
	Timer as TimerIcon,
	HowToVote as VoteIcon,
} from "@mui/icons-material";
import {
	Alert,
	AppBar,
	Box,
	Button,
	Chip,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	LinearProgress,
	Paper,
	Snackbar,
	TextField,
	Toolbar,
	Typography,
} from "@mui/material";
import type React from "react";
import { useState } from "react";
import { useRetro } from "../context/RetroContext";
import { ExportResults } from "./ExportResults";
import { ItemList } from "./ItemList";
import { ParticipantsModal } from "./ParticipantsModal";

export function SessionView() {
	const {
		state,
		addItem,
		updateItem,
		mergeItems,
		voteItem,
		setPhase,
		closeSession,
		setTimer,
		timerAction,
		deleteTimer,
	} = useRetro();
	const [selectedItems, setSelectedItems] = useState<string[]>([]);
	const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
	const [mergeText, setMergeText] = useState("");
	const [snackbar, setSnackbar] = useState<{
		open: boolean;
		message: string;
		severity: "success" | "error";
	}>({
		open: false,
		message: "",
		severity: "success",
	});
	const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
	const [participantsModalOpen, setParticipantsModalOpen] = useState(false);
	const [setTimerOpen, setSetTimerOpen] = useState(false);
	const [duration, setDuration] = useState(5);

	// Removed drag and drop sensors since we're using selection mode only

	if (!state.session) {
		return null;
	}

	const { session } = state;
	// Always in selection mode during merge phase
	const selectionMode = session.phase === "merge";
	const goodItems = Object.values(session.items).filter(
		(item) => item.category === "good",
	);
	const badItems = Object.values(session.items).filter(
		(item) => item.category === "bad",
	);

	// Removed drag handlers since we're using selection mode only

	const handleMerge = async () => {
		if (selectedItems.length < 2) return;

		try {
			await mergeItems(selectedItems, mergeText);
			setMergeDialogOpen(false);
			setSelectedItems([]);
			setMergeText("");
			showSnackbar("Items merged successfully!", "success");
		} catch (_error) {
			showSnackbar("Failed to merge items", "error");
		}
	};

	const handleItemSelect = (itemId: string) => {
		if (selectedItems.includes(itemId)) {
			setSelectedItems(selectedItems.filter((id) => id !== itemId));
		} else {
			setSelectedItems([...selectedItems, itemId]);
		}
	};

	const handleManualMerge = () => {
		if (selectedItems.length < 2) {
			showSnackbar("Please select at least 2 items to merge", "error");
			return;
		}

		// Create default merge text from all selected items
		const allTexts = selectedItems
			.map((id) => {
				const item = session.items[id];
				return item?.originalTexts
					? item.originalTexts.join(" + ")
					: item?.text || "";
			})
			.filter(Boolean);

		setMergeText(allTexts.join(" + "));
		setMergeDialogOpen(true);
	};

	const handleVote = async (itemId: string) => {
		try {
			await voteItem(itemId);
			showSnackbar("Vote recorded!", "success");
		} catch (_error) {
			showSnackbar("Failed to vote", "error");
		}
	};

	const handlePhaseChange = async (newPhase: typeof session.phase) => {
		try {
			await setPhase(newPhase);
			showSnackbar(`Phase changed to ${newPhase}`, "success");
		} catch (_error) {
			showSnackbar("Failed to change phase", "error");
		}
	};

	const handleCloseSession = async () => {
		try {
			await closeSession();
			showSnackbar("Session cancelled", "success");
			setCloseConfirmOpen(false);
		} catch (_error) {
			showSnackbar("Failed to cancel session", "error");
		}
	};

	const handleCloseSessionClick = () => {
		setCloseConfirmOpen(true);
	};

	const copySessionLink = () => {
		const url = `${window.location.origin}?session=${session.id}`;
		navigator.clipboard.writeText(url);
		showSnackbar("Session link copied to clipboard!", "success");
	};

	const showSnackbar = (message: string, severity: "success" | "error") => {
		setSnackbar({ open: true, message, severity });
	};

	// Timer helper functions
	const formatTime = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	};

	const getProgress = (): number => {
		if (!session.timer) return 0;
		return (
			((session.timer.duration - session.timer.remaining) /
				session.timer.duration) *
			100
		);
	};

	const handleSetTimer = async () => {
		await setTimer(duration * 60);
		setSetTimerOpen(false);
	};

	const getPhaseButton = (
		phase: typeof session.phase,
		label: string,
		icon: React.ReactNode,
	) => {
		const isActive = session.phase === phase;
		const isHost = session.creatorId === state.userId;
		const isDisabled =
			!isHost ||
			(phase === "merge" && goodItems.length + badItems.length < 2) ||
			(phase === "vote" && goodItems.length + badItems.length === 0) ||
			(phase === "results" && goodItems.length + badItems.length === 0);

		return (
			<Button
				variant={isActive ? "contained" : "outlined"}
				color={isActive ? "primary" : "inherit"}
				startIcon={icon}
				onClick={() => handlePhaseChange(phase)}
				disabled={isDisabled}
				size="small"
				title={!isHost ? "Only the session host can change phases" : undefined}
			>
				{label}
			</Button>
		);
	};

	return (
		<Box sx={{ flexGrow: 1 }}>
			<AppBar position="static">
				<Toolbar>
					<Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
						Retrospective Session
					</Typography>

					{/* Timer in AppBar */}
					{session.timer ? (
						<Box display="flex" alignItems="center" gap={1} sx={{ mr: 2 }}>
							<TimerIcon sx={{ color: "white", fontSize: 20 }} />
							<Typography variant="body2" sx={{ color: "white", minWidth: 50 }}>
								{formatTime(session.timer.remaining)}
							</Typography>
							{session.creatorId === state.userId && (
								<Box display="flex" gap={0.5}>
									<IconButton
										size="small"
										color="inherit"
										onClick={() =>
											timerAction(session.timer?.isActive ? "stop" : "start")
										}
										title={
											session.timer.isActive ? "Pause timer" : "Start timer"
										}
									>
										{session.timer.isActive ? <PauseIcon /> : <PlayIcon />}
									</IconButton>
									<IconButton
										size="small"
										color="inherit"
										onClick={() => timerAction("reset")}
										title="Reset timer"
									>
										<RefreshIcon />
									</IconButton>
									<IconButton
										size="small"
										color="inherit"
										onClick={deleteTimer}
										title="Delete timer"
									>
										<DeleteIcon />
									</IconButton>
								</Box>
							)}
						</Box>
					) : (
						session.creatorId === state.userId && (
							<Button
								variant="outlined"
								size="small"
								startIcon={<TimerIcon />}
								onClick={() => setSetTimerOpen(true)}
								sx={{
									mr: 2,
									color: "white",
									borderColor: "rgba(255,255,255,0.5)",
									"&:hover": { borderColor: "white" },
								}}
							>
								Timer
							</Button>
						)
					)}

					<Chip
						label={`${session.participants.length} participant${session.participants.length !== 1 ? "s" : ""}`}
						color="primary"
						size="small"
						clickable
						onClick={() => setParticipantsModalOpen(true)}
						sx={{ mr: 1, cursor: "pointer" }}
					/>
					<Chip
						label={`Phase: ${session.phase}`}
						color="secondary"
						size="small"
						sx={{ mr: 2 }}
					/>

					<IconButton
						color="inherit"
						onClick={copySessionLink}
						title="Copy session link"
					>
						<ShareIcon />
					</IconButton>

					<ExportResults
						session={session}
						users={session.users}
						isHost={session.creatorId === state.userId}
					/>

					{/* Only show cancel button to session creator */}
					{session.creatorId === state.userId && (
						<IconButton
							color="inherit"
							onClick={handleCloseSessionClick}
							title="Cancel session"
						>
							<CloseIcon />
						</IconButton>
					)}
				</Toolbar>

				{/* Timer progress bar */}
				{session.timer?.isActive && (
					<LinearProgress
						variant="determinate"
						value={getProgress()}
						sx={{
							height: 3,
							backgroundColor: "rgba(255,255,255,0.2)",
							"& .MuiLinearProgress-bar": {
								backgroundColor: "white",
							},
						}}
					/>
				)}
			</AppBar>

			<Container
				maxWidth="xl"
				sx={{
					py: 2,
					height: "calc(100vh - 64px)",
					display: "flex",
					flexDirection: "column",
				}}
			>
				{/* Compact Phase Controls */}
				<Box
					sx={{
						px: 2,
						py: 1.5,
						mb: 2,
						borderBottom: "1px solid #e0e0e0",
						backgroundColor: "#f8f9fa",
					}}
				>
					<Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
						<Typography variant="subtitle2" sx={{ mr: 1, fontWeight: "bold" }}>
							Phases:
						</Typography>
						<Box display="flex" gap={1} alignItems="center">
							{getPhaseButton("input", "Input", <MergeIcon />)}
							{getPhaseButton("merge", "Merge", <MergeIcon />)}
							{getPhaseButton("vote", "Vote", <VoteIcon />)}
							{getPhaseButton("results", "Results", <ResultsIcon />)}
						</Box>

						{/* Selection count and merge button */}
						{session.phase === "merge" &&
							session.creatorId === state.userId && (
								<>
									<Box
										sx={{
											width: 1,
											height: 20,
											borderLeft: "1px solid #e0e0e0",
											mx: 1,
										}}
									/>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ mr: 1 }}
									>
										{selectedItems.length} selected
									</Typography>
									<Button
										variant="contained"
										color="primary"
										size="small"
										onClick={handleManualMerge}
										disabled={selectedItems.length < 2}
										startIcon={<MergeIcon />}
										sx={{ minWidth: 120 }}
									>
										Merge
									</Button>
								</>
							)}
					</Box>
				</Box>

				{/* Main Content */}
				<Box
					display="flex"
					flexDirection={{ xs: "column", lg: "row" }}
					gap={2}
					flex={1}
				>
					<Box flex={1} minWidth={300}>
						<ItemList
							items={goodItems}
							category="good"
							phase={session.phase}
							userId={state.userId}
							users={session.users}
							onAddItem={addItem}
							onVote={handleVote}
							onUpdateItem={updateItem}
							selectionMode={selectionMode}
							selectedItems={selectedItems}
							onItemSelect={handleItemSelect}
						/>
					</Box>

					<Box flex={1} minWidth={300}>
						<ItemList
							items={badItems}
							category="bad"
							phase={session.phase}
							userId={state.userId}
							users={session.users}
							onAddItem={addItem}
							onVote={handleVote}
							onUpdateItem={updateItem}
							selectionMode={selectionMode}
							selectedItems={selectedItems}
							onItemSelect={handleItemSelect}
						/>
					</Box>
				</Box>
			</Container>

			{/* Merge Dialog */}
			<Dialog
				open={mergeDialogOpen}
				onClose={() => setMergeDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Merge Items</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Combine the selected items into one:
					</Typography>

					{/* Show items being merged */}
					<Box sx={{ mb: 2 }}>
						{selectedItems.map((itemId) => {
							const item = session.items[itemId];
							if (!item) return null;

							return (
								<Paper
									key={itemId}
									sx={{
										p: 1,
										mb: 1,
										bgcolor: "grey.50",
										border: "1px solid #e0e0e0",
									}}
								>
									<Typography variant="body2">
										{item.originalTexts
											? item.originalTexts.join(" + ")
											: item.text}
									</Typography>
									{item.originalTexts && item.originalTexts.length > 1 && (
										<Typography variant="caption" color="text.secondary">
											(Already merged from {item.originalTexts.length} items)
										</Typography>
									)}
								</Paper>
							);
						})}
					</Box>

					<TextField
						fullWidth
						multiline
						rows={3}
						value={mergeText}
						onChange={(e) => setMergeText(e.target.value)}
						placeholder="Enter the merged text..."
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
					<Button
						onClick={handleMerge}
						variant="contained"
						disabled={!mergeText.trim()}
					>
						Merge {selectedItems.length} Items
					</Button>
				</DialogActions>
			</Dialog>

			{/* Cancel Session Confirmation Dialog */}
			<Dialog
				open={closeConfirmOpen}
				onClose={() => setCloseConfirmOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Cancel Session</DialogTitle>
				<DialogContent>
					<Typography variant="body1">
						Are you sure you want to cancel this retrospective session? This
						action cannot be undone and will end the session for all
						participants. All session data will no longer be visible.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCloseConfirmOpen(false)}>
						Keep Session
					</Button>
					<Button
						onClick={handleCloseSession}
						variant="contained"
						color="error"
					>
						Cancel Session
					</Button>
				</DialogActions>
			</Dialog>

			{/* Participants Modal */}
			<ParticipantsModal
				open={participantsModalOpen}
				onClose={() => setParticipantsModalOpen(false)}
				users={session.users}
				participants={session.participants}
				currentUserId={state.userId}
			/>

			{/* Set Timer Dialog */}
			<Dialog
				open={setTimerOpen}
				onClose={() => setSetTimerOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Set Timer</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 1 }}>
						<TextField
							fullWidth
							label="Duration (minutes)"
							type="number"
							value={duration}
							onChange={(e) =>
								setDuration(Math.max(1, parseInt(e.target.value, 10) || 1))
							}
							inputProps={{ min: 1, max: 60 }}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSetTimerOpen(false)}>Cancel</Button>
					<Button onClick={handleSetTimer} variant="contained">
						Set Timer
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbar */}
			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					sx={{ width: "100%" }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
}
